import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './Gantt.module.css';

const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getRectangleClass = (startYear, startMonth, startDay, durationDays) => {
    const currentDate = new Date();
    const taskStartDate = new Date(startYear, startMonth, startDay);
    const taskEndDate = new Date(startYear, startMonth, startDay + durationDays - 1);

    if (taskEndDate < currentDate) return styles.pastCampaign;
    if (taskStartDate <= currentDate && taskEndDate >= currentDate) return styles.activeCampaign;
    return styles.futureCampaign;
};

const Gantt = ({ months, data, onCampaignSelect, selectedOrderlineId, onCampaignDoubleClick }) => {
    const [selectedTask, setSelectedTask] = useState(null); 

    useEffect(() => {
        setSelectedTask(selectedOrderlineId);
    }, [selectedOrderlineId]);

    const calculateShadedBoxWidth = useCallback(() => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const totalDaysInYear = 365 + (isLeapYear(currentYear) ? 1 : 0);
        const dayOfYear = (currentDate - new Date(currentYear, 0, 0)) / 86400000;
        return (dayOfYear / totalDaysInYear) * 100;
    }, []);

    const [shadedBoxWidth, setShadedBoxWidth] = useState(calculateShadedBoxWidth());

    useEffect(() => {
        const handleResize = () => setShadedBoxWidth(calculateShadedBoxWidth());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateShadedBoxWidth]);

    const handleTaskClick = useCallback((task) => {
        setSelectedTask(task.id);
        if (onCampaignSelect) onCampaignSelect(task);
    }, [onCampaignSelect]);

    const handleTaskDoubleClick = useCallback((task) => {
        setSelectedTask(task.id);
        if (onCampaignDoubleClick) onCampaignDoubleClick(task);
    }, [onCampaignDoubleClick]);

    const renderTaskRow = useCallback((task) => {
        let remainingDays = task.durationDays;
        let currentMonthIndex = task.startMonth;
        let currentDayInMonth = task.startDay;

        const totalColumns = months.length;
        let totalWidth = 0;
        let startPercentage = 0;

        // Calculate the total width of the rectangle in percentage
        for (let x = currentMonthIndex; x < totalColumns && remainingDays > 0; x++) {
            const daysInThisMonth = daysInMonth(task.startYear, x);

            if (x === currentMonthIndex) {
                const daysLeftInMonth = daysInThisMonth - currentDayInMonth + 1;
                const daysInMonthSpan = Math.min(remainingDays, daysLeftInMonth);

                startPercentage = ((currentDayInMonth - 1) / daysInThisMonth) * 100;
                totalWidth += (daysInMonthSpan / daysInThisMonth) * 100;

                remainingDays -= daysInMonthSpan;
            } else {
                const daysInMonthSpan = Math.min(remainingDays, daysInThisMonth);
                totalWidth += (daysInMonthSpan / daysInThisMonth) * 100;
                remainingDays -= daysInMonthSpan;
            }
        }

        const rectangleClass = getRectangleClass(task.startYear, task.startMonth, task.startDay, task.durationDays);
        const isSelected = selectedTask === task.id;

        return months.map((_, index) => (
            <td key={index} className={styles.monthCell} style={{ position: 'relative' }}>
                {index === currentMonthIndex && (
                    <React.Fragment>
                        <div
                            className={`${styles.dayRectangle} ${rectangleClass} ${isSelected ? styles.highlighted : ''}`}
                            style={{ left: `${startPercentage}%`, width: `${totalWidth}%` }}
                            onClick={() => handleTaskClick(task)}
                            onDoubleClick={() => handleTaskDoubleClick(task)}
                        />
                        <div
                            className={styles.taskDescription}
                            style={{ left: `${startPercentage}%`, width: `${totalWidth}%` }}
                        >
                            {task.description}
                        </div>
                    </React.Fragment>
                )}
            </td>
        ));
    }, [months, selectedTask, handleTaskClick]);

    const memoizedTaskRows = useMemo(() => data.map(task => (
        <tr key={task.id}>{renderTaskRow(task)}</tr>
    )), [data, renderTaskRow]);

    return (
        <div className={styles.tableContainer}>
            <div className={styles.shadedBox} style={{ width: `${shadedBoxWidth}%` }} />
            <table className={styles.monthDaysTable}>
                <thead>
                    <tr>
                        {months.map((month, index) => (
                            <th key={index} className={styles.monthHeader}>
                                {month.slice(0, 3)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>{memoizedTaskRows}</tbody>
            </table>
        </div>
    );
};

Gantt.propTypes = {
    months: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        startYear: PropTypes.number.isRequired,
        startMonth: PropTypes.number.isRequired,
        startDay: PropTypes.number.isRequired,
        durationDays: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired
    })).isRequired,
    onCampaignSelect: PropTypes.func,
    selectedOrderlineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Gantt;